// ConfirmDialog - Replacement for window.confirm()

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../../icons';
import './ConfirmDialog.css';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
    message: '',
  });

  // Store resolve function in a ref to avoid dependency issues
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({
        ...options,
        isOpen: true,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  if (!state.isOpen) {
    return (
      <ConfirmContext.Provider value={{ confirm }}>
        {children}
      </ConfirmContext.Provider>
    );
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {createPortal(
        <div className="confirm-overlay" onClick={handleCancel}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{state.title || 'Confirm'}</h2>
              <button className="modal-close" onClick={handleCancel}><Icons.X /></button>
            </div>
            <div className="modal-content">
              <div className={`confirm-dialog confirm-dialog--${state.variant || 'info'}`}>
                <p className="confirm-dialog__message">{state.message}</p>
                <div className="confirm-dialog__actions">
                  <button className="btn btn-secondary" onClick={handleCancel}>
                    {state.cancelText || 'Cancel'}
                  </button>
                  <button
                    className={`btn ${state.variant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
                    onClick={handleConfirm}
                  >
                    {state.confirmText || 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm(): ConfirmContextType {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}
